import React from 'react';
import {
    Card,
    CardContent,
    Avatar,
    Chip,
    Typography,
    Box,
    alpha,
    useTheme,
} from '@mui/material';

/**
 * A single KPI stat card with a vibrant, rich full-background gradient,
 * matching a modern, premium dashboard aesthetic.
 *
 * Props
 *   title    – label below the value
 *   value    – number or string to display
 *   icon     – MUI icon element
 *   color    – hex accent colour (used for the background gradient)
 *   subtitle – small secondary text (optional)
 *   trend    – number (positive = green, negative = red, null = hidden)
 */
const StatCard = ({ title, value, icon, color, subtitle, trend }) => {
    const theme = useTheme();
    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                background: theme.palette.background.paper,
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                position: 'relative',
                overflow: 'hidden',
                color: theme.palette.text.primary,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 20px -8px ${alpha(color, 0.3)}`,
                },
            }}
        >
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                {/* Icon + trend chip */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                    }}
                >
                    <Avatar
                        sx={{
                            bgcolor: alpha(color, 0.1),
                            color: color,
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                        }}
                    >
                        {icon}
                    </Avatar>

                    {trend != null && (
                        <Chip
                            label={`${trend > 0 ? '+' : ''}${trend}%`}
                            size="small"
                            sx={{
                                bgcolor: alpha(color, 0.1),
                                color: color,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                borderRadius: '8px',
                            }}
                        />
                    )}
                </Box>

                {/* Title */}
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.75rem' }}
                >
                    {title}
                </Typography>

                {/* Value */}
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 800,
                        mb: 0.5,
                        letterSpacing: '-1px',
                        color: 'text.primary',
                    }}
                >
                    {typeof value === 'number' ? value.toLocaleString() : value}
                </Typography>

                {/* Subtitle */}
                {subtitle && (
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default StatCard;
